import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../components/collapsibleSection";
import Input from "components/input";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useUpdate } from "@refinedev/core";
import { IHomepageDetailData } from "interfaces";
import { z } from "zod";
import Dropzone from "components/dropzone";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import RadioButton from "components/radioButton";

interface IBannerField extends IHeroBannerForm {
  id: string;
}

interface IHeroBannerForm {
  banner_url: string;
  video_url: string;
  open_url: string;
  type: string;
}

const EditHeroBannerSchema = z
  .object({
    banner_url: z.string(),
    video_url: z.string(),
    open_url: z.string(),
    type: z.string(),
  })
  .superRefine(({ type, banner_url, video_url }, ctx) => {
    if (type === "image" && !banner_url) {
      ctx.addIssue({
        path: ["banner_url"],
        code: "custom",
        message: "This field is required",
      });
    }
    if (type === "video" && !video_url) {
      ctx.addIssue({
        path: ["video_url"],
        code: "custom",
        message: "This field is required",
      });
    }
  });

const EditHeroBannersSchema = z.object({
  data: z.array(EditHeroBannerSchema),
});

export const HeroBannerForm = ({
  defaultData,
}: {
  defaultData: IHomepageDetailData["ads-banner"]["hero-banner"];
}) => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<
    { data: IHeroBannerForm[] },
    HttpError,
    { data: IHeroBannerForm[] }
  >({
    defaultValues: {
      data: defaultData.data.map((data) => ({
        banner_url: data.type === "image" ? data.banner : "",
        video_url: data.type === "video" ? data.banner : "",
        open_url: data.open_url ?? "",
        type: data.type,
      })),
    },
    resolver: zodResolver(EditHeroBannersSchema),
  });

  const { fields } = useFieldArray({
    control,
    name: "data",
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: { data: IHeroBannerForm[] }) => {
    const submitValues = [...values.data].map((value) => ({
      banner: value.type === "image" ? value.banner_url : value.video_url,
      open_url: value.type === "image" ? value.open_url ?? "" : "",
      type: value.type,
    }));
    mutate(
      {
        resource: `content/homepage`,
        id: `${defaultData.id}/hero-banner/ads-banner/update`,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          data: submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/mkt/homepage/detail?default=banner`);
        },
      }
    );
  };

  return (
    <CollapsibleSection label="Hero Banner" defaultOpen={false}>
      <form
        className="divide-y divide-gray-100"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full px-4 py-6 sm:px-6 divide-y divide-gray-300">
          <div className="flex flex-col gap-y-6 divide-y divide-gray-300">
            {fields.map((formField: IBannerField, index: number) => (
              <div
                className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 py-4"
                key={formField.id}
              >
                <div className="sm:col-span-3">
                  <div>
                    <div className="mb-4">
                      <Controller
                        control={control}
                        name={`data.${index}.type`}
                        render={({ field: { onChange, value = "" } }) => {
                          return (
                            <RadioButton
                              checked={value === "image"}
                              onChange={() => onChange("image")}
                              item={{
                                id: "image",
                                title: "Image",
                              }}
                            />
                          );
                        }}
                      />
                    </div>
                    <Label id="banner" name="Banner (1440x600)" />
                    <Controller
                      control={control}
                      name={`data.${index}.banner_url`}
                      render={({ field: { onChange, value = "" } }) => {
                        return (
                          <Dropzone
                            id={`data.${index}.banner_url`}
                            validator={{
                              width: 1440,
                              height: 600,
                            }}
                            onChange={(imageUrl: string) => onChange(imageUrl)}
                            value={value}
                          />
                        );
                      }}
                    />
                    {get(errors.data, `${index}.banner_url`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors.data, `${index}.banner_url.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="mt-4 sm:mt-6">
                    <Input
                      id="open_url"
                      type="text"
                      placeholder="Banner URL"
                      error={get(errors.data, `${index}.open_url`, "")}
                      {...register(`data.${index}.open_url`)}
                    />
                    {get(errors.data, `${index}.open_url`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors.data, `${index}.open_url.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="sm:col-span-3">
                  <div>
                    <div className="mb-4">
                      <Controller
                        control={control}
                        name={`data.${index}.type`}
                        render={({ field: { onChange, value = "" } }) => {
                          return (
                            <RadioButton
                              checked={value === "video"}
                              onChange={() => onChange("video")}
                              item={{
                                id: "video",
                                title: "Video",
                              }}
                            />
                          );
                        }}
                      />
                    </div>
                    <Label id="video_url" name="URL" />
                    <Controller
                      name={`data.${index}.video_url`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          id={`video_url_${formField.id}`}
                          type="text"
                          placeholder="URL"
                          error={get(errors.data, `${index}.video_url`, "")}
                          {...field}
                        />
                      )}
                    />
                    {get(errors.data, `${index}.video_url`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors.data, `${index}.video_url.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </CollapsibleSection>
  );
};
